import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import { useProduct } from "@shopify/hydrogen-react";

import { CompareAtPriceDisplay } from "@components/Price/constants";
import { ProductHeroBaseView } from "@react/sections/ProductHero/ProductHeroBase/ProductHeroBase.view";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  isMigratedBundle,
  isStandaloneBundleKit,
  isVariantFixedBundle,
} from "@react/utilities";
import {
  type ProductHeroSettings,
  type OPProductVariantConnection,
} from "@root/tablescape/state";
import {
  TablescapeContext,
  TablescapeEvent,
} from "@root/tablescape/utilities/tablescapeContext";
import { PageModal } from "@tablescape/state";
import { formatProduct, formatProductVariant } from "@tablescape/utilities";

const ProductHeroBase = () => {
  const [rootMarginOffset, setRootMarginOffset] = useState(0);
  const { product, selectedVariant, setSelectedVariant } = useProduct();

  const {
    brandValueProps,
    breadcrumbs,
    productIntro: { showReviews },
    productOverview: { content, heading },
    productStickyBar: { desktopEnabled, mobileEnabled },
    settings: {
      productSelectorExperience,
      showBundleReviewsAggregateDisclaimer,
      theme,
    },
    sizeGuide: { enabled: sizeGuideEnabled, linkText: sizeGuideLinkText },
    text: {
      breadcrumbsLabel,
      closeAriaLabel,
      formatBtnText,
      giftWithPurchase,
      homeLabel,
      linkAriaLabel,
      linkReadLess,
      linkReadMore,
      productBridgeLabel,
      productSelectorText: { selectOption },
      ratingHero,
      videoLabel,
    },
  } = useSectionSettings<ProductHeroSettings>();

  const formattedProduct = useMemo(() => formatProduct(product), [product]);

  const formattedSelectedVariant = useMemo(
    () => formatProductVariant(selectedVariant),
    [selectedVariant]
  );

  const inViewProps = useMemo(
    () => ({
      initialInView: true,
      rootMargin: `-${rootMarginOffset}px 0px 0px`,
      threshold: 0.12,
    }),
    [rootMarginOffset]
  );

  const isStickyProductBarEnabled = useMemo(() => {
    return desktopEnabled || mobileEnabled;
  }, [desktopEnabled, mobileEnabled]);

  const showReviewsFootnote = useMemo(() => {
    return (
      showReviews &&
      showBundleReviewsAggregateDisclaimer &&
      (isVariantFixedBundle([formattedSelectedVariant]) ||
        isMigratedBundle(formattedProduct) ||
        isStandaloneBundleKit(formattedSelectedVariant))
    );
  }, [
    showReviews,
    showBundleReviewsAggregateDisclaimer,
    formattedSelectedVariant,
    formattedProduct,
  ]);

  const showCompareAtPriceValueFootnote = useMemo(() => {
    return (
      !!formattedSelectedVariant.compareAtPrice &&
      formattedSelectedVariant.compareAtPriceDisplay ===
        CompareAtPriceDisplay.VALUE
    );
  }, [
    formattedSelectedVariant.compareAtPriceDisplay,
    formattedSelectedVariant.compareAtPrice,
  ]);

  const showComboSelectorExperience = useMemo(() => {
    return (
      !!formattedProduct.productSelectorId &&
      productSelectorExperience === "combo_selector"
    );
  }, [formattedProduct.productSelectorId, productSelectorExperience]);

  const setMatchedVariant = useCallback(
    (data?: {
      productSelector: {
        selectedVariantId?: OPProductVariantConnection["id"];
      };
    }) => {
      const selectedVariantId = data?.productSelector.selectedVariantId;
      const matchedVariant = product.variants.nodes.find((variant) => {
        return variant.id === selectedVariantId;
      });
      if (matchedVariant) {
        setSelectedVariant(matchedVariant);
      }
    },
    [setSelectedVariant, product.variants.nodes]
  );

  const sizeGuideModalHandler = useCallback(() => {
    TablescapeContext.emit(TablescapeEvent.TOGGLE_PAGE_MODAL, {
      ui: {
        pageModalTarget: PageModal.SIZE_GUIDE_MODAL,
      },
    });
  }, []);

  useLayoutEffect(() => {
    if (!isStickyProductBarEnabled) {
      return;
    }

    const announcementBarHeight =
      document
        .querySelector(`[id^="shopify-section"][id$="tbsp-announcement-bar"]`)
        ?.getBoundingClientRect?.().height ?? 0;

    const topNavigationHeight =
      document
        .querySelector(`[id^="shopify-section"][id$="tbsp-navigation"]`)
        ?.getBoundingClientRect?.().height ?? 0;

    setRootMarginOffset(announcementBarHeight + topNavigationHeight);
  }, [isStickyProductBarEnabled]);

  useEffect(() => {
    const productSelectorVariantSelectedListener = TablescapeContext.on(
      TablescapeEvent.PRODUCT_SELECTOR_VARIANT_SELECTED,
      setMatchedVariant
    );

    TablescapeContext.emit(TablescapeEvent.PRODUCT_HERO_VARIANT_SELECTED, {
      productHero: {
        product: formattedProduct,
        selectedVariant: formattedSelectedVariant,
      },
    });

    return () => {
      productSelectorVariantSelectedListener.off(
        TablescapeEvent.PRODUCT_SELECTOR_VARIANT_SELECTED,
        setMatchedVariant
      );
    };
  }, [setMatchedVariant, formattedProduct, formattedSelectedVariant]);

  if (!product) {
    return null;
  }

  return (
    <ProductHeroBaseView
      brandValueProps={brandValueProps}
      breadcrumbs={breadcrumbs}
      breadcrumbsLabel={breadcrumbsLabel}
      closeAriaLabel={closeAriaLabel}
      content={content}
      formatBtnText={formatBtnText}
      giftWithPurchase={giftWithPurchase}
      heading={heading}
      homeLabel={homeLabel}
      inViewProps={inViewProps}
      isStickyProductBarEnabled={isStickyProductBarEnabled}
      linkAriaLabel={linkAriaLabel}
      linkReadLess={linkReadLess}
      linkReadMore={linkReadMore}
      product={formatProduct(product)}
      productBridgeLabel={sizeGuideLinkText ?? productBridgeLabel}
      productBridgeLabelCtaHandler={
        sizeGuideEnabled ? sizeGuideModalHandler : undefined
      }
      ratingHero={ratingHero}
      selectOption={selectOption}
      showComboSelectorExperience={showComboSelectorExperience}
      showCompareAtPriceValueFootnote={showCompareAtPriceValueFootnote}
      showReviewsFootnote={showReviewsFootnote}
      theme={theme}
      videoLabel={videoLabel}
    />
  );
};

export const ProductHero = memo(ProductHeroBase);
